import React, { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

const toDraft = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : String(value)

/** '' → null, comma decimals accepted, anything unparsable → undefined. */
const parseDraft = (draft: string): number | null | undefined => {
  const trimmed = draft.trim().replace(',', '.')
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

interface BlueprintDimensionInputProps {
  label: string
  value: number | null | undefined
  onCommit: (value: number | null) => void
  error?: string
  readOnly?: boolean
  placeholder?: string
  /** Unit suffix (default "cm"). */
  unit?: string
  /** Short label shown above the input on hover/focus. */
  hint?: string
  className?: string
  style?: React.CSSProperties
}

/**
 * Tiny inline cm input. Commits on blur/Enter only — committing per
 * keystroke would redraw (and in fill mode rescale) the blueprint, moving
 * the input under the cursor.
 */
export function BlueprintDimensionInput({
  label,
  value,
  onCommit,
  error,
  readOnly,
  placeholder,
  unit = 'cm',
  hint,
  className,
  style,
}: BlueprintDimensionInputProps) {
  const hintEl = hint ? (
    <span className="pointer-events-none absolute -top-3.5 left-0 whitespace-nowrap text-[10px] leading-none text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
      {hint}
    </span>
  ) : null
  const [draft, setDraft] = useState(toDraft(value))
  const focused = useRef(false)

  // Resync from the form when not editing (e.g. form.reset after the query
  // loads, or COMPOSITE extent updates).
  useEffect(() => {
    if (!focused.current) setDraft(toDraft(value))
  }, [value])

  const commit = () => {
    const parsed = parseDraft(draft)
    if (parsed === undefined) {
      setDraft(toDraft(value))
      return
    }
    setDraft(toDraft(parsed))
    if (parsed !== (value ?? null)) onCommit(parsed)
  }

  if (readOnly) {
    return (
      <div
        className={cn(
          'group pointer-events-auto relative flex h-5 w-[56px] items-center justify-end rounded-sm border border-dashed border-slate-300 bg-white/90 px-1 text-xs tabular-nums text-slate-600',
          className
        )}
        style={style}
        title={`${label} (derived from modules)`}
        aria-label={label}
      >
        {hintEl}
        {toDraft(value) || '–'}
        <span className="ml-0.5 text-[10px] text-muted-foreground">{unit}</span>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'group pointer-events-auto relative h-5 w-[56px]',
        className
      )}
      style={style}
    >
      {hintEl}
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        aria-invalid={!!error || undefined}
        title={error ? `${label}: ${error}` : label}
        placeholder={placeholder}
        value={draft}
        className={cn(
          'h-5 w-full rounded-sm border border-slate-300 bg-white/90 px-1 pr-5 text-right text-xs tabular-nums text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-500 focus:ring-1 focus:ring-slate-400',
          error && 'border-destructive ring-1 ring-destructive'
        )}
        onFocus={e => {
          focused.current = true
          e.currentTarget.select()
        }}
        onBlur={() => {
          focused.current = false
          commit()
        }}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            // Don't submit the surrounding form.
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            setDraft(toDraft(value))
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault()
            const step = (e.shiftKey ? 10 : 1) * (e.key === 'ArrowUp' ? 1 : -1)
            const current = parseDraft(draft) ?? value ?? 0
            setDraft(toDraft(Math.max(0, (current ?? 0) + step)))
          }
        }}
      />
      <span className="pointer-events-none absolute inset-y-0 right-1 flex items-center text-[10px] text-muted-foreground">
        {unit}
      </span>
    </div>
  )
}
